<script setup>
import { ref, computed } from 'vue'
import { useForm } from '@inertiajs/vue3'

import Modal       from '@pages/Shared/Modal.vue'
import Select      from '@pages/Shared/form/Select.vue'
import MultiSelect from '@pages/Shared/form/MultiSelect.vue'
import Input       from '@pages/Shared/form/Input.vue'
import Spinner     from '@pages/Shared/ui/Spinner.vue'

import { flashToast } from '@/toast.js'

const props = defineProps({
    modelValue:   { type: Boolean, required: true },
    assessments:  { type: Array,   required: true },
    sessions:     { type: Array,   required: true },
    terms:        { type: Array,   required: true },
    classes:      { type: Array,   required: true },
    sessionTerms: { type: Array,   required: true },
    types:        { type: Array,   required: true },
})

const emit = defineEmits(['update:modelValue'])

const isEditing         = ref(false)
const editingAssessment = ref(null)

const form = useForm({
    session_term_id:     null,
    academic_session_id: null,
    academic_term_id:    null,
    name:                '',
    type_id:             null,
    weight:              1,
    class_room_ids:      [],
    is_active:           true,
})

const typeOptions = computed(() =>
    props.types.map(t => ({ label: `${t.name} (${t.code})`, value: t.id }))
)

const classOptions = computed(() =>
    props.classes.map(c => ({ label: c.name, value: c.id }))
)

const sessionTermOptions = computed(() => {
    const map = new Map()
    props.sessionTerms.forEach(st => map.set(st.value, { label: st.label, value: st.value }))

    if (isEditing.value && editingAssessment.value) {
        const key = `${editingAssessment.value.academic_session.id}|${editingAssessment.value.academic_term.id}`
        if (!map.has(key)) {
            map.set(key, {
                label: `${editingAssessment.value.academic_session.name} - ${editingAssessment.value.academic_term.name} (inactive)`,
                value: key,
            })
        }
    }

    return Array.from(map.values())
})

// Called by Index via ref for edit
const open = (assessment) => {
    isEditing.value         = true
    editingAssessment.value = assessment
    form.clearErrors()

    form.name            = assessment.name
    form.type_id         = assessment.type?.id ?? null
    form.weight          = Number(assessment.weight)
    form.session_term_id = `${assessment.academic_session.id}|${assessment.academic_term.id}`
    form.class_room_ids  = assessment.classes.map(c => c.id)
    form.is_active       = assessment.is_active

    emit('update:modelValue', true)
}

const handleModalClose = () => {
    form.reset()
    form.clearErrors()
    isEditing.value         = false
    editingAssessment.value = null
    emit('update:modelValue', false)
}

const handleSubmit = () => {
    if (!form.session_term_id) {
        return flashToast({ type: 'error', message: 'Please select session and term' })
    }

    const [sessionId, termId] = form.session_term_id.split('|')
    form.academic_session_id  = sessionId
    form.academic_term_id     = termId

    const duplicate = props.assessments.some(a =>
        a.name.toLowerCase() === form.name.toLowerCase() &&
        a.academic_session?.id == sessionId &&
        a.academic_term?.id == termId &&
        (!isEditing.value || a.id !== editingAssessment.value?.id)
    )

    if (duplicate) {
        return flashToast({
            type:    'error',
            message: 'An assessment with this name already exists for the selected session and term.',
        })
    }

    const url    = isEditing.value
        ? `/staff/manage-assessments/${editingAssessment.value.id}`
        : '/staff/manage-assessments'
    const method = isEditing.value ? 'put' : 'post'

    form.submit(method, url, {
        onSuccess: (page) => {
            handleModalClose()
            flashToast(page.props.flash)
        },
        onError: () => flashToast({ type: 'error', message: 'Check form for errors' }),
    })
}

defineExpose({ open })
</script>

<template>
    <Modal
        :model-value="modelValue"
        :title="isEditing ? 'Edit Assessment' : 'Create Assessment'"
        size="medium"
        @close="handleModalClose"
    >
        <form @submit.prevent="handleSubmit" class="p-6 space-y-5">
            <Select
                v-model="form.session_term_id"
                :options="sessionTermOptions"
                label="Session / Term"
                placeholder="Select session and term"
                :error="form.errors.academic_session_id || form.errors.academic_term_id"
            />
            <MultiSelect
                v-model="form.class_room_ids"
                :options="classOptions"
                label="Classes"
                placeholder="Select classes"
                :error="form.errors.class_room_ids"
            />
            <Input
                v-model="form.name"
                label="Assessment Name"
                placeholder="e.g., First CA, Mid-Term Exam"
                :error="form.errors.name"
            />
            <Select
                v-model="form.type_id"
                :options="typeOptions"
                label="Assessment Type"
                placeholder="Select assessment type"
                :error="form.errors.type_id"
            />
            <Input
                type="number"
                v-model="form.weight"
                label="Weight (%)"
                placeholder="20"
                min="1"
                max="100"
                :error="form.errors.weight"
            />

            <!-- Active toggle -->
            <div class="flex items-center justify-between py-2 border-t border-gray-100">
                <div>
                    <label class="text-sm font-medium text-gray-700">Active Status</label>
                    <p class="text-xs text-gray-500 mt-0.5">Only active assessments can receive scores</p>
                </div>
                <button type="button" @click="form.is_active = !form.is_active"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                    :class="form.is_active ? 'bg-edit' : 'bg-gray-300'">
                    <span class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                        :class="form.is_active ? 'translate-x-6' : 'translate-x-1'" />
                </button>
            </div>

            <div class="flex flex-col gap-3 pt-4 border-t border-gray-100 sm:flex-row">
                <button type="submit" :disabled="form.processing"
                    class="w-full px-5 py-2.5 text-sm font-medium text-white rounded-lg transition
                        disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                    :class="isEditing ? 'bg-edit hover:bg-edit-light' : 'bg-primary hover:bg-primary-light'">
                    <Spinner v-if="form.processing" :size="14" />
                    {{ isEditing ? 'Update Assessment' : 'Create Assessment' }}
                </button>
            </div>
        </form>
    </Modal>
</template>